import { useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

export interface ProcessQueryParamOptions {
  keepParam?: boolean;
}

export const useProcessQueryParam = (
  paramName: string,
  processFn: (value: string) => Promise<void>,
  options?: ProcessQueryParamOptions
) => {
  const location = useLocation();
  const history = useHistory();

  // Keep a stable reference to the latest function so we don't trigger 
  // the useEffect unnecessarily if the parent passes an inline function
  const processFnRef = useRef(processFn);
  processFnRef.current = processFn;

  // Track the value we are currently processing to avoid firing 
  // the async function multiple times before the URL is cleared.
  const processingValueRef = useRef<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paramValue = params.get(paramName);

    // If the param exists and we aren't already processing this exact value
    if (paramValue && processingValueRef.current !== paramValue) {
      processingValueRef.current = paramValue;

      processFnRef.current(paramValue)
        .catch(err => {
          console.error(`Failed to process query param "${paramName}":`, err);
        })
        .finally(() => {
          if (!options?.keepParam) {
            // Get the freshest params from history in case the URL 
            // changed while the async function was running
            const currentParams = new URLSearchParams(history.location.search);
            currentParams.delete(paramName);
            
            const newSearch = currentParams.toString() ? `?${currentParams.toString()}` : '';
            
            history.replace({
              pathname: history.location.pathname,
              search: newSearch,
              hash: history.location.hash,
              state: history.location.state
            });
          }
        });
    }
    
    // If the param is no longer in the URL, reset our tracking ref
    // so it can be triggered again if the user navigates back to it
    if (!paramValue) {
      processingValueRef.current = null;
    }
  }, [location.search, history, paramName, options?.keepParam]);
};
